import { useCallback } from 'react';

import { useRouterActions } from 'react-resource-router-next';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { addRovoParamsToUrl } from '@atlaskit/rovo-triggers/params';
import {
	ExperienceName,
	PreviewContentStage,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { ContentType } from '@atlassian/conversation-assistant-store/controllers/preview-content/contentType';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';

/** What triggered the open. All three behave identically; only the analytics attribute differs. */
export type OpenStagedContentPreviewSource = 'previewCard' | 'hyperlink' | 'smartLink';

export type OpenStagedContentPreviewParams = {
	conversationId: string | undefined;
	messageId: string;
	invocationId: string;
	contentId: string | undefined;
	// `null` is the preview card's "not yet resolved" value.
	contentType: ContentType | null | undefined;
	actionKey: string | undefined;
	isMauiPreview: boolean;
	isJiraCreateAction: boolean;
	isJsmCreateAction: boolean;
	stagingAreaEnabled: boolean | undefined;
	fullScreen: boolean | undefined;
	product: string | undefined;
	source: OpenStagedContentPreviewSource;
};

/**
 * Opens the staging area on a staged item, as if its Preview button was clicked. Shared by the
 * preview card and by staged-content link clicks.
 */
export const useOpenStagedContentPreview = (): ((
	params: OpenStagedContentPreviewParams,
) => void) => {
	const experienceTracker = useAIMateExperienceTracker();
	const { sendAnalyticsEvent } = useAnalytics();
	const publish = usePublish('ai-mate');
	const { push } = useRouterActions();
	const { setStagingAreaMode, setMessageIdSelectedForPreview, setInvocationIdSelectedForPreview } =
		useStagingAreaActions();

	return useCallback(
		(params: OpenStagedContentPreviewParams) => {
			const {
				conversationId,
				messageId,
				invocationId,
				contentId,
				contentType,
				actionKey,
				isMauiPreview,
				isJiraCreateAction,
				isJsmCreateAction,
				stagingAreaEnabled,
				fullScreen,
				product,
				source,
			} = params;

			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'smartCreatePreviewCard',
				attributes: {
					...(conversationId !== undefined ? { conversationId } : {}),
					messageId,
					invocationId: invocationId || undefined,
					contentType,
					// Omitted for the Preview button so its events are unchanged.
					...(source !== 'previewCard' ? { source } : {}),
				},
			});
			experienceTracker.start({
				name: ExperienceName.PREVIEW_CONTENT,
				id: invocationId,
				timeout: 30000,
				attributes: {
					expectedContentType: contentType,
					// Action keys without a content type mapping all collapse into one bucket.
					actionKey,
					// Separates content never created from content that failed to load.
					contentId,
					invocationId,
					[PreviewContentStage.STARTED_AT]: performance.now(),
				},
			});

			if (invocationId && !isJiraCreateAction && !(isJsmCreateAction && fg('surveys-aifc'))) {
				setMessageIdSelectedForPreview(null);
				setInvocationIdSelectedForPreview(invocationId);
			} else {
				setInvocationIdSelectedForPreview(null);
				setMessageIdSelectedForPreview(messageId);
			}

			const isInAtlassianHome =
				window.location.href.includes('home.atlassian.com') ||
				window.location.href.includes('home.stg.atlassian.com');

			if (fullScreen && isInAtlassianHome) {
				// If we are in Atlassian home and already fullscreen, render the staging area next to chat
				setStagingAreaMode('inline', 'rovoChatPreviewCard');
			} else if (isInAtlassianHome) {
				// If we are in Atlassian home and not already in fullscreen chat, redirect to fullscreen chat and set the staging area to be open
				const path = addRovoParamsToUrl('', {
					pathway: 'chat',
					conversationId: conversationId ?? undefined,
					stagingAreaOpen: true,
					messageIdSelectedForPreview: messageId,
					invocationIdSelectedForPreview: invocationId ?? undefined,
				});
				push(`/chat${path}`);
			} else if (!stagingAreaEnabled) {
				if (
					isMauiPreview &&
					contentType !== ContentType.slides &&
					contentType !== ContentType.database
				) {
					if (product === 'confluence') {
						// When in Confluence, open the product-specific AIFC modal so the
						// Ephemeral Preview can stay mounted when the Rovo side chat closes.
						publish({
							type: 'open-smart-creation-modal',
							source: 'rovo-chat-preview-card',
							data: {
								channelId: conversationId,
								entryPoint: 'rovoChatPreviewCard',
								stagingAreaActive: true,
								isFloating: true,
								shouldShowBackButton: false,
							},
						});
					} else {
						setStagingAreaMode('ephemeralModal', 'rovoChatPreviewCard');
					}
				} else {
					setStagingAreaMode('modal', 'rovoChatPreviewCard');
				}
			}
		},
		[
			sendAnalyticsEvent,
			experienceTracker,
			setMessageIdSelectedForPreview,
			setInvocationIdSelectedForPreview,
			setStagingAreaMode,
			push,
			publish,
		],
	);
};
